import React, { useEffect, useReducer, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Form from 'react-bootstrap/Form';
import 'react-bootstrap-typeahead/css/Typeahead.css';
import { FaRegSave } from 'react-icons/fa';
import { MdAdd, MdArrowBack, MdKeyboardReturn } from 'react-icons/md';
import { useLocation, useSearchParams } from 'react-router-dom';
import { toast } from '../../toasts';
import CustomTypeahead from '../../components/CustomTypeahead';
import { Button, Checkbox, FormField, Section, controlClass, textareaClass } from '../../components/EntityList';
import DetailPageLoader from '../../components/DetailPageLoader/DetailPageLoader';
import AssetService from '../../services/asset.service';
import AccountService from '../../services/account.service';
import UserService from '../../services/user.service';
import { getError } from '../../utils';
import { useLabels } from '../../hooks/useLabels';

const CURRENCIES = ['USD', 'UYU'];

const FieldError = ({ message }) =>
	message ? <p className='tw:mt-1 tw:mb-0 tw:text-[12px] tw:text-danger'>{message}</p> : null;

FieldError.propTypes = {
	message: PropTypes.string,
};

const emptyForm = {
	name: '',
	type: '',
	ubication: '',
	housingUnit: '',
	description: '',
	price: '',
	currency: '',
	folder: '',
	guaranteedAmount: '',
	owner: null,
	responsible: null,
	multipleLots: false,
	lot: '',
	observations: '',
};

const reducer = (state, action) => {
	switch (action.type) {
	case 'FETCH_REQUEST':
		return { ...state, loading: true, error: '' };
	case 'FETCH_SUCCESS':
		return { ...state, loading: false, error: '' };
	case 'FETCH_FAIL':
		return { ...state, loading: false, error: action.payload };
	case 'SAVE_REQUEST':
		return { ...state, saving: true, error: '' };
	case 'SAVE_SUCCESS':
		return { ...state, saving: false, error: '' };
	case 'SAVE_FAIL':
		return { ...state, saving: false, error: action.payload };
	default:
		return state;
	}
};

const assetToForm = (asset) => ({
	...emptyForm,
	name: asset?.name || '',
	type: asset?.type || '',
	ubication: asset?.ubication || '',
	housingUnit: asset?.housingUnit || '',
	description: asset?.description || '',
	price: asset?.price ?? '',
	currency: asset?.currency || '',
	folder: asset?.folder || '',
	guaranteedAmount: asset?.guaranteedAmount ?? '',
	owner: asset?.owner || null,
	responsible: asset?.responsible || null,
	multipleLots: !!asset?.multipleLots,
	lot: asset?.multipleLots ? '' : (asset?.lot || ''),
	observations: asset?.observations || '',
});

const AssetEditor = ({
	mode = 'create',
	assetId = null,
	initialData = null,
	onCancel,
	onSaved,
	cancelLabel = 'Cancelar',
	saveLabel,
	onReturn,
	returnLabel,
	disableOwner = false,
	preloadOwner = null,
	allowCreateRoundTrip = false,
}) => {
	const { t } = useLabels();
	const isEditing = mode === 'edit';
	const location = useLocation();
	const [searchParams, setSearchParams] = useSearchParams();
	// Id injected via ?accountId= after a "crear nueva cuenta" round trip.
	// Only reachable when allowCreateRoundTrip is on — the two modal call
	// sites (AssetDetails.jsx, AccountAssets.jsx) keep the create link off
	// entirely, since navigating away would destroy their unsaved state.
	const injectedAccountId = searchParams.get('accountId');
	const consumedAccountId = useRef(null);
	// Stable snapshot for the edit-mode hydration guard below — syncInjectedParam
	// rewrites the live param as the user edits, so the guard must key off this
	// value as it was at mount, not the (now-mutable) injectedAccountId above.
	const injectedAccountIdAtMount = useRef(injectedAccountId);

	// Keep the URL mirroring the current selection so a manual pick or clear
	// doesn't leave a stale id from an earlier round trip to be re-injected.
	const syncInjectedParam = (key, id) => {
		const nextValue = id || null;
		if (searchParams.get(key) === nextValue) return;
		if (nextValue) consumedAccountId.current = nextValue;
		setSearchParams((prev) => {
			const next = new URLSearchParams(prev);
			if (nextValue) next.set(key, nextValue);
			else next.delete(key);
			return next;
		}, { replace: true });
	};

	const [{ loading, saving, error }, dispatch] = useReducer(reducer, {
		loading: isEditing && !initialData,
		saving: false,
		error: '',
	});
	const [form, setForm] = useState(() => {
		const base = assetToForm(initialData);
		if (!isEditing && preloadOwner) {
			return {
				...base,
				owner: preloadOwner,
				currency: base.currency || preloadOwner.currency || '',
				folder: base.folder || preloadOwner.folder || '',
			};
		}
		return base;
	});
	const [accounts, setAccounts] = useState([]);
	const [users, setUsers] = useState([]);
	const [fieldErrors, setFieldErrors] = useState({});

	useEffect(() => {
		let isCurrent = true;

		const fetchData = async () => {
			dispatch({ type: 'FETCH_REQUEST' });
			try {
				const requests = [AccountService.listForAssets(), UserService.listForAssets()];
				if (isEditing && assetId && !initialData) {
					requests.unshift(AssetService.getDetails(assetId));
				}
				const responses = await Promise.all(requests);
				if (!isCurrent) {
					return;
				}

				if (isEditing && assetId && !initialData) {
					const [assetRes, accountsRes, usersRes] = responses;
					// Skip the owner hydration when an account was just injected via
					// ?accountId= (see the effect below) — otherwise whichever writer
					// resolves last wins nondeterministically.
					if (injectedAccountIdAtMount.current) {
						const hydrated = assetToForm(assetRes.data);
						setForm((prev) => ({ ...hydrated, owner: prev.owner }));
					} else {
						setForm(assetToForm(assetRes.data));
					}
					setAccounts(accountsRes.data);
					setUsers(usersRes.data);
				} else {
					const [accountsRes, usersRes] = responses;
					setAccounts(accountsRes.data);
					setUsers(usersRes.data);
				}
				dispatch({ type: 'FETCH_SUCCESS' });
			} catch (ex) {
				const message = getError(ex);
				if (isCurrent) {
					dispatch({ type: 'FETCH_FAIL', payload: message });
				}
				toast.error(isEditing ? 'No se pudo obtener activo' : 'No se pudieron obtener las cuentas');
			}
		};

		fetchData();
		return () => {
			isCurrent = false;
		};
	}, [assetId, initialData, isEditing]);

	// A preloadOwner that arrives after mount (account-scoped "Nuevo activo" fetches
	// the account asynchronously) needs to be synced in separately — the useState
	// initializer above only sees it if it was already available at mount time.
	useEffect(() => {
		if (!isEditing && preloadOwner) {
			setForm((prev) => ({
				...prev,
				owner: preloadOwner,
				currency: prev.currency || preloadOwner.currency || '',
				folder: prev.folder || preloadOwner.folder || '',
			}));
		}
	}, [preloadOwner, isEditing]);

	// Auto-select an account created via the "crear nueva cuenta" link, which
	// returns here with ?accountId=<id>. accounts is already refetched fresh
	// on mount (see fetchData above), so find-in-list is enough.
	useEffect(() => {
		if (!injectedAccountId || consumedAccountId.current === injectedAccountId) return;
		const injectedAccount = accounts.find((acc) => acc._id === injectedAccountId);
		if (!injectedAccount) return;
		setForm((prev) => ({
			...prev,
			owner: injectedAccount,
			currency: prev.currency || injectedAccount.currency || '',
			folder: prev.folder || injectedAccount.folder || '',
		}));
		setFieldErrors((prevErrors) => {
			if (!prevErrors.owner) return prevErrors;
			const nextErrors = { ...prevErrors };
			delete nextErrors.owner;
			return nextErrors;
		});
		consumedAccountId.current = injectedAccountId;
	}, [accounts, injectedAccountId]);

	const updateField = (field, value) => {
		setForm((prevForm) => ({
			...prevForm,
			[field]: value,
		}));
		setFieldErrors((prevErrors) => {
			if (!prevErrors[field]) return prevErrors;
			const nextErrors = { ...prevErrors };
			delete nextErrors[field];
			return nextErrors;
		});
	};

	const handleMultipleLotsChange = (checked) => {
		setForm((prevForm) => ({
			...prevForm,
			multipleLots: checked,
			lot: checked ? '' : prevForm.lot,
		}));
		setFieldErrors((prevErrors) => {
			if (!prevErrors.lot) return prevErrors;
			const nextErrors = { ...prevErrors };
			delete nextErrors.lot;
			return nextErrors;
		});
	};

	const REQUIRED_FIELD_MESSAGE = 'Este campo es obligatorio.';

	const FIELD_LABELS = {
		name: 'Nombre',
		owner: t('entity.assetOwner'),
		lot: t('field.lotUpper'),
	};

	const getFieldErrors = () => {
		const errors = {};
		if (!form.name.trim()) errors.name = REQUIRED_FIELD_MESSAGE;
		if (!form.owner) errors.owner = REQUIRED_FIELD_MESSAGE;
		if (!form.multipleLots && !form.lot.trim()) errors.lot = REQUIRED_FIELD_MESSAGE;
		return errors;
	};

	const buildPayload = () => ({
		name: form.name,
		type: form.type,
		ubication: form.ubication,
		housingUnit: form.housingUnit,
		description: form.description,
		price: Number(form.price) || 0,
		currency: form.currency,
		folder: form.folder,
		guaranteedAmount: Number(form.guaranteedAmount) || 0,
		owner: form.owner?._id || form.owner,
		responsible: form.responsible?._id || form.responsible,
		multipleLots: !!form.multipleLots,
		...(form.multipleLots ? {} : { lot: form.lot }),
		observations: form.observations,
	});

	const handleSubmit = async (event) => {
		event.preventDefault();
		if (loading || saving) {
			return;
		}

		const errors = getFieldErrors();
		const missingFields = Object.keys(errors);
		if (missingFields.length > 0) {
			setFieldErrors(errors);
			const missingLabels = missingFields.map((field) => FIELD_LABELS[field] || field);
			dispatch({
				type: 'SAVE_FAIL',
				payload: `Complete el/los siguiente/s campo/s requerido/s: ${missingLabels.join(', ')}.`,
			});
			return;
		}
		setFieldErrors({});

		dispatch({ type: 'SAVE_REQUEST' });
		try {
			const payload = buildPayload();
			let savedAssetId = assetId || initialData?._id;
			if (isEditing) {
				const result = await AssetService.edit(savedAssetId, payload);
				savedAssetId = result.data?.asset?._id || savedAssetId;
				toast.success(t('msg.assetUpdated'));
			} else {
				const result = await AssetService.create(payload);
				savedAssetId = result.data?.asset?._id;
				toast.success(t('msg.assetCreated'));
			}
			dispatch({ type: 'SAVE_SUCCESS' });
			if (onSaved) {
				onSaved(savedAssetId);
			}
		} catch (ex) {
			const message = getError(ex);
			dispatch({ type: 'SAVE_FAIL', payload: message });
			console.error(message);
			toast.error(isEditing ? t('msg.assetEditFailed') : 'Error al crear activo');
		}
	};

	if (loading) {
		return <DetailPageLoader entityLabel='editor' />;
	}

	const resolvedSaveLabel = saveLabel || (isEditing ? 'Guardar' : 'Agregar');

	return (
		<Form id='asset-editor-form' onSubmit={handleSubmit} className='tw:space-y-4'>
			<div className='tw:sticky tw:top-14 tw:z-20 tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-3 tw:rounded-panel tw:border tw:border-border tw:bg-surface/95 tw:px-5 tw:py-3.5 tw:shadow-sm tw:backdrop-blur'>
				<div className='tw:text-[12.5px] tw:text-ink-2'>
					{isEditing ? 'Actualizá los datos del activo.' : 'Completá los datos para crear el activo.'}
				</div>
				<div className='tw:flex tw:gap-2'>
					<Button icon={<MdArrowBack />} onClick={onCancel} disabled={saving}>
						{cancelLabel}
					</Button>
					{onReturn && (
						<Button icon={<MdKeyboardReturn />} onClick={onReturn} disabled={saving}>
							{returnLabel || t('action.back')}
						</Button>
					)}
					<Button variant='primary' icon={isEditing ? <FaRegSave /> : <MdAdd />} type='submit' disabled={saving || loading}>
						{saving ? 'Guardando...' : resolvedSaveLabel}
					</Button>
				</div>
			</div>

			{error && (
				<div className='tw:rounded-[7px] tw:border tw:border-danger tw:bg-danger-soft tw:px-4 tw:py-3 tw:text-[13px] tw:text-danger'>
					{error}
				</div>
			)}

			<Section title='Datos generales'>
				<div className='tw:grid tw:grid-cols-1 tw:gap-4 md:tw:grid-cols-2'>
					<FormField id='asset-name' label={t('field.name')} required>
						<Form.Control
							id='asset-name'
							className={controlClass}
							value={form.name}
							onChange={(event) => updateField('name', event.target.value)}
						/>
						<FieldError message={fieldErrors.name} />
					</FormField>
					<FormField id='asset-type' label={t('field.type')}>
						<Form.Control
							id='asset-type'
							className={controlClass}
							value={form.type}
							onChange={(event) => updateField('type', event.target.value)}
						/>
					</FormField>
					<FormField id='asset-ubication' label={t('field.ubicationPlain')}>
						<Form.Control
							id='asset-ubication'
							className={controlClass}
							value={form.ubication}
							onChange={(event) => updateField('ubication', event.target.value)}
						/>
					</FormField>
					<FormField id='asset-housing-unit' label={t('field.housingUnit')}>
						<Form.Control
							id='asset-housing-unit'
							className={controlClass}
							value={form.housingUnit}
							onChange={(event) => updateField('housingUnit', event.target.value)}
						/>
						<p className='tw:mt-1 tw:mb-0 tw:text-[11.5px] tw:text-ink-3'>{t('msg.multipleUnitsTooltip')}</p>
					</FormField>
					<FormField id='asset-description' label={t('field.descriptionPlain')} className='md:tw:col-span-2'>
						<Form.Control
							id='asset-description'
							className={controlClass}
							value={form.description}
							onChange={(event) => updateField('description', event.target.value)}
						/>
					</FormField>
				</div>
			</Section>

			<Section title='Valuación'>
				<div className='tw:grid tw:grid-cols-1 tw:gap-4 md:tw:grid-cols-2'>
					<FormField id='asset-price' label={t('field.price')}>
						<Form.Control
							id='asset-price'
							type='number'
							min={0}
							className={controlClass}
							value={form.price}
							onChange={(event) => updateField('price', event.target.value)}
						/>
					</FormField>
					<FormField id='asset-currency' label={t('field.currency')}>
						<Form.Select
							id='asset-currency'
							className={controlClass}
							value={form.currency}
							onChange={(event) => updateField('currency', event.target.value)}
						>
							<option value=''>Elegí Moneda</option>
							{CURRENCIES.map((currency) => (
								<option key={currency} value={currency}>
									{currency}
								</option>
							))}
						</Form.Select>
					</FormField>
					<FormField id='asset-folder' label={t('field.folder')}>
						<Form.Control
							id='asset-folder'
							className={controlClass}
							value={form.folder}
							onChange={(event) => updateField('folder', event.target.value)}
						/>
					</FormField>
					<FormField id='asset-guaranteed-amount' label={t('field.guaranteedAmount')}>
						<Form.Control
							id='asset-guaranteed-amount'
							type='number'
							min={0}
							className={controlClass}
							value={form.guaranteedAmount}
							onChange={(event) => updateField('guaranteedAmount', event.target.value)}
						/>
					</FormField>
				</div>
			</Section>

			<Section title='Propietario y responsable'>
				<div className='tw:grid tw:grid-cols-1 tw:gap-4 md:tw:grid-cols-2'>
					<FormField id='asset-owner' label={t('entity.assetOwner')} required>
						<CustomTypeahead
							id='asset-owner'
							labelKey={(option) => `${option.name}`}
							onChange={(selected) => {
								const nextOwner = selected && selected.length > 0 ? selected[0] : null;
								updateField('owner', nextOwner);
								syncInjectedParam('accountId', nextOwner?._id);
							}}
							options={accounts}
							selected={form.owner ? [form.owner] : []}
							placeholder={t('form.placeholder.searchOwnerAccount')}
							linkToCreate={allowCreateRoundTrip
								? `/AdminScreen/newAccount?returnTo=${encodeURIComponent(location.pathname + location.search)}`
								: ''}
							newOption='nueva cuenta'
							disabled={disableOwner || saving}
							variant='entity'
						/>
						<FieldError message={fieldErrors.owner} />
					</FormField>
					<FormField id='asset-responsible' label={t('field.responsible')}>
						<CustomTypeahead
							id='asset-responsible'
							labelKey={(option) => `${option.name}`}
							onChange={(selected) => updateField('responsible', selected && selected.length > 0 ? selected[0] : null)}
							options={users}
							selected={form.responsible ? [form.responsible] : []}
							placeholder={t('form.placeholder.searchResponsibleUser')}
							disabled={saving}
							variant='entity'
						/>
					</FormField>
				</div>
			</Section>

			<Section title='Padrón'>
				<div className='tw:grid tw:grid-cols-1 tw:gap-4 md:tw:grid-cols-2 tw:items-end'>
					<div className='tw:flex tw:items-center tw:h-[34px]'>
						<Checkbox
							label={t('form.multipleLotsQ')}
							checked={form.multipleLots}
							onChange={(event) => handleMultipleLotsChange(event.target.checked)}
						/>
					</div>
					<FormField id='asset-lot' label={t('field.lotUpper')} required={!form.multipleLots}>
						<Form.Control
							id='asset-lot'
							className={controlClass}
							value={form.lot}
							disabled={form.multipleLots}
							onChange={(event) => updateField('lot', event.target.value)}
						/>
						<FieldError message={fieldErrors.lot} />
					</FormField>
				</div>
			</Section>

			<Section title='Observaciones' description='Notas internas o información adicional del activo.'>
				<FormField id='asset-observations' label={t('field.observations')}>
					<Form.Control
						id='asset-observations'
						as='textarea'
						rows={8}
						className={textareaClass}
						value={form.observations}
						onChange={(event) => updateField('observations', event.target.value)}
					/>
				</FormField>
			</Section>
		</Form>
	);
};

AssetEditor.propTypes = {
	mode: PropTypes.oneOf(['create', 'edit']),
	assetId: PropTypes.string,
	initialData: PropTypes.object,
	onCancel: PropTypes.func.isRequired,
	onSaved: PropTypes.func.isRequired,
	cancelLabel: PropTypes.string,
	saveLabel: PropTypes.string,
	onReturn: PropTypes.func,
	returnLabel: PropTypes.string,
	disableOwner: PropTypes.bool,
	preloadOwner: PropTypes.object,
	allowCreateRoundTrip: PropTypes.bool,
};

export default AssetEditor;
